import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import { IoClose as Close } from 'react-icons/io5';
import { BsTrash3 as TrashIcon } from 'react-icons/bs';
import {
    FiAlertTriangle,
    FiArrowRight,
    FiCheck,
    FiUsers,
    FiShield,
    FiInfo
} from 'react-icons/fi';
import LabelCardService from '@Helpers/URLHelpers/WeboutService/LabelCardService';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/Views/RegistryLinkViewService';
import ErrorHandler from '@Root/ErrorHandler';
import './DeleteLabelCardForm.scss';

export default function DeleteLabelCardForm(props) {
    const [cursor, setCursor] = useState('auto');
    const [newLabelId, setNewLabelId] = useState('');
    const [registryLinkCount, setRegistryLinkCount] = useState(0);
    const [deleting, setDeleting] = useState(false);

    const labelCardService = new LabelCardService();
    const registryLinkViewService = new RegistryLinkViewService();
    const errorHandler = new ErrorHandler();

    const className = 'deleteLabelCardForm';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        setCursor('wait');

        try {
            const response =
                await registryLinkViewService.getCountByTargetTypeAndTargetId(
                    'labelCard',
                    props?.item?.id
                );

            if (response.status < 400) {
                setRegistryLinkCount(parseInt(response.body, 10));
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setCursor('auto');
        }
    };

    const timeout = (delay) =>
        new Promise((resolve) => setTimeout(resolve, delay));

    const handleDeleteTemplate = async () => {
        setCursor('wait');
        setDeleting(true);

        try {
            const body = {
                id: props?.item?.id,
                newLabelId: newLabelId || 0
            };

            const response = await labelCardService.deleteLabelCard(body);

            if (response.status < 400) {
                await timeout(500);
                props.reloadData();
                props.closePopup();
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setDeleting(false);
            setCursor('auto');
        }
    };

    const hasContacts = registryLinkCount > 0;

    const availableLabels = useMemo(
        () =>
            props?.labelCardList?.filter(
                (item) => item.id !== props?.item?.id
            ) || [],
        [props?.labelCardList, props?.item?.id]
    );

    const selectedLabel = availableLabels.find(
        (item) => item.id === newLabelId
    );

    const canDelete = !deleting && (!hasContacts || newLabelId !== '');

    return (
        <Dialog
            open={props.open}
            onClose={deleting ? undefined : props.closePopup}
            maxWidth="sm"
            fullWidth
            className={className}
            PaperProps={{
                className: `${className}__paper`
            }}
            style={{ cursor }}
        >

            <Box className={`${className}__header`}>
                <Box className={`${className}__brand`}>
                    <Box className={`${className}__brand-icon`}>
                        <TrashIcon />
                    </Box>

                    <Box>
                        <Typography className={`${className}__eyebrow`}>
                            Delete filter
                        </Typography>

                        <Typography className={`${className}__title`}>
                            {props?.item?.name}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={props.closePopup}
                    disabled={deleting}
                    className={`${className}__close`}
                    size="small"
                >
                    <Close />
                </IconButton>
            </Box>

            <Box className={`${className}__body`}>

                <Box className={`${className}__danger`}>
                    <Box className={`${className}__danger-icon`}>
                        <FiAlertTriangle />
                    </Box>

                    <Box className={`${className}__danger-content`}>
                        <Typography className={`${className}__danger-title`}>
                            This action cannot be undone
                        </Typography>

                        <Typography className={`${className}__danger-text`}>
                            Once deleted, this filter and its configuration
                            will be permanently removed.
                        </Typography>
                    </Box>
                </Box>

                {hasContacts ? (
                    <Box className={`${className}__contacts`}>
                        <Box className={`${className}__contacts-header`}>
                            <Box className={`${className}__contacts-heading`}>
                                <Box className={`${className}__contacts-icon`}>
                                    <FiUsers />
                                </Box>

                                <Box>
                                    <Typography
                                        className={`${className}__section-title`}
                                    >
                                        Contacts need to be transferred
                                    </Typography>

                                    <Typography
                                        className={`${className}__section-text`}
                                    >
                                        Move these contacts to another filter
                                        before deleting.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box className={`${className}__contact-badge`}>
                                <strong>{registryLinkCount}</strong>
                                <span>
                                    {registryLinkCount === 1
                                        ? 'contact'
                                        : 'contacts'}
                                </span>
                            </Box>
                        </Box>

                        <Box className={`${className}__transfer`}>
                            <Box className={`${className}__step`}>
                                <Typography
                                    className={`${className}__step-label`}
                                >
                                    FROM
                                </Typography>

                                <Box className={`${className}__filter-box`}>
                                    <Box
                                        className={`${className}__filter-dot`}
                                    />

                                    <Typography
                                        className={`${className}__filter-name`}
                                    >
                                        {props?.item?.name}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box className={`${className}__transfer-arrow`}>
                                <FiArrowRight />
                            </Box>

                            <Box className={`${className}__step`}>
                                <Typography
                                    className={`${className}__step-label`}
                                >
                                    MOVE TO
                                </Typography>

                                <FormControl
                                    fullWidth
                                    className={`${className}__form-control`}
                                >
                                    <Select
                                        value={newLabelId}
                                        onChange={(event) =>
                                            setNewLabelId(
                                                event.target.value
                                            )
                                        }
                                        displayEmpty
                                        disabled={deleting}
                                        className={`${className}__select`}
                                        MenuProps={{
                                            PaperProps: {
                                                className: `${className}__menu`
                                            }
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            Choose a filter
                                        </MenuItem>

                                        {availableLabels.map((item) => (
                                            <MenuItem
                                                value={item.id}
                                                key={item.id}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {selectedLabel && (
                            <Box className={`${className}__selection`}>
                                <Box className={`${className}__selection-icon`}>
                                    <FiCheck />
                                </Box>

                                <Typography
                                    className={`${className}__selection-text`}
                                >
                                    {registryLinkCount}{' '}
                                    {registryLinkCount === 1
                                        ? 'contact'
                                        : 'contacts'}{' '}
                                    will be moved to{' '}
                                    <strong>{selectedLabel.name}</strong>.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box className={`${className}__safe`}>
                        <Box className={`${className}__safe-icon`}>
                            <FiShield />
                        </Box>

                        <Box className={`${className}__safe-content`}>
                            <Typography className={`${className}__safe-title`}>
                                Nothing needs to be transferred
                            </Typography>

                            <Typography className={`${className}__safe-text`}>
                                This filter has no linked contacts, so it can
                                be deleted immediately.
                            </Typography>
                        </Box>

                        <FiCheck className={`${className}__safe-check`} />
                    </Box>
                )}
                <Box className={`${className}__note`}>
                    <FiInfo />

                    <Typography>
                        Deleting a filter does not delete the contacts
                        themselves.
                    </Typography>
                </Box>
            </Box>
            <Box className={`${className}__footer`}>
                <Box className={`${className}__footer-status`}>
                    {hasContacts && !newLabelId ? (
                        <>
                            <FiAlertTriangle />
                            <span>Select a destination filter</span>
                        </>
                    ) : (
                        <>
                            <FiShield />
                            <span>
                                {hasContacts
                                    ? 'Contacts will be preserved'
                                    : 'No contacts affected'}
                            </span>
                        </>
                    )}
                </Box>

                <Box className={`${className}__actions`}>
                    <Button
                        onClick={props.closePopup}
                        disabled={deleting}
                        className={`${className}__cancel`}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        startIcon={
                            deleting ? (
                                <CircularProgress
                                    size={16}
                                    color="inherit"
                                />
                            ) : (
                                <TrashIcon />
                            )
                        }
                        onClick={handleDeleteTemplate}
                        disabled={!canDelete}
                        className={`${className}__delete`}
                    >
                        {deleting ? 'Deleting…' : 'Delete filter'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
